import { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import DatePicker from '../components/common/CustomCalendar.jsx';

const FACING_LABELS = {
  gi: 'GI – First Sending',
  gi2: 'GI – 2-Day Prep',
  'gi-reminder-12h': 'GI – 12h Unopened Reminder',
  'gi-reminder-24h': 'GI – 12h Unopened Reminder',
  'gi-reminder-48h': 'GI – 48h Reminder',
  mayo1: 'Mayo Pre-Visit',
  mayo2: 'Mayo TAVR',
  mayo3: 'Mayo Angiogram',
  mayo4: 'Mayo Post-Discharge',
};

const TABS = [
  { id: 'video', label: 'Video & Flow' },
  { id: 'avatars', label: 'Avatars' },
  { id: 'dropoff', label: 'Drop-off Analysis' },
  { id: 'satisfaction', label: 'Satisfaction' },
  { id: 'report', label: 'Executive Report' },
  { id: 'smsLogs', label: 'SMS Logs' },
];

export default function AdminStatisticsPage() {
  const [activeTab, setActiveTab] = useState('video');
  const [videoData, setVideoData] = useState(null);
  const [avatarData, setAvatarData] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [smsLogs, setSmsLogs] = useState([]);
  const [smsLogsLoading, setSmsLogsLoading] = useState(false);
  const [smsLogsError, setSmsLogsError] = useState(null);
  const [smsCaseNumber, setSmsCaseNumber] = useState('');
  const [smsLogProcedureStartDate, setSmsLogProcedureStartDate] = useState('');
  const [smsLogProcedureEndDate, setSmsLogProcedureEndDate] = useState('');
  const [videoLoading, setVideoLoading] = useState(true);
  const [avatarLoading, setAvatarLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(true);
  const [videoError, setVideoError] = useState(null);
  const [avatarError, setAvatarError] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [appliedStartDate, setAppliedStartDate] = useState(null);
  const [appliedEndDate, setAppliedEndDate] = useState(null);
  const loadRequestIdRef = useRef(0);

  const toIsoDate = (value) => {
    if (!value) return null;
    const trimmed = typeof value === 'string' ? value.trim() : value;
    if (!trimmed) return null;

    if (trimmed instanceof Date) {
      const year = trimmed.getFullYear();
      const month = String(trimmed.getMonth() + 1).padStart(2, '0');
      const day = String(trimmed.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    if (typeof trimmed === 'string') {
      if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
        return trimmed.slice(0, 10);
      }

      const parts = trimmed.split('/');
      if (parts.length === 3) {
        const [month, day, year] = parts;
        const yyyy = year.length === 2 ? `20${year}` : year;
        return `${yyyy.padStart(4, '0')}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    return null;
  };

  const handleStartDateChange = (value) => {
    setStartDate(toIsoDate(value));
  };

  const handleEndDateChange = (value) => {
    setEndDate(toIsoDate(value));
  };

  const loadStatistics = useCallback((rangeStart, rangeEnd) => {
    const requestId = ++loadRequestIdRef.current;
    const params = {};
    const start = toIsoDate(rangeStart);
    const end = toIsoDate(rangeEnd);

    if (start) params.startDate = start;
    if (end) params.endDate = end;

    setVideoLoading(true);
    setAvatarLoading(true);
    setReportLoading(true);

    axios.get('/api/admin/video-statistics', { params })
      .then(r => {
        if (loadRequestIdRef.current !== requestId) return;
        setVideoData(r.data.data);
        setVideoLoading(false);
      })
      .catch(e => {
        if (loadRequestIdRef.current !== requestId) return;
        setVideoError(e.message);
        setVideoLoading(false);
      });

    axios.get('/api/admin/avatar-statistics', { params })
      .then(r => {
        if (loadRequestIdRef.current !== requestId) return;
        setAvatarData(r.data.data);
        setAvatarLoading(false);
      })
      .catch(e => {
        if (loadRequestIdRef.current !== requestId) return;
        setAvatarError(e.message);
        setAvatarLoading(false);
      });

    axios.get('/api/admin/statistics/report', { params })
      .then(r => {
        if (loadRequestIdRef.current !== requestId) return;
        setReportData(r.data.data);
        setReportLoading(false);
      })
      .catch(e => {
        if (loadRequestIdRef.current !== requestId) return;
        setReportError(e.message);
        setReportLoading(false);
      });
  }, []);

  const loadSmsLogs = useCallback(async (rangeStart, rangeEnd, caseNumber, procedureStartDate, procedureEndDate) => {
    setSmsLogsLoading(true);
    setSmsLogsError(null);
    const params = {};
    const start = toIsoDate(rangeStart);
    const end = toIsoDate(rangeEnd);
    if (start) params.startDate = start;
    if (end) params.endDate = end;
    if (caseNumber?.trim()) params.caseNumber = caseNumber.trim();
    if (procedureStartDate) params.procedureStartDate = procedureStartDate;
    if (procedureEndDate) params.procedureEndDate = procedureEndDate;
    try {
      const { data } = await axios.get('/api/admin/sms-logs', { params });
      setSmsLogs(data.data || []);
    } catch (error) {
      setSmsLogsError(error.message);
    } finally {
      setSmsLogsLoading(false);
    }
  }, []);

  const handleSearchDates = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    loadStatistics(startDate, endDate);
    if (activeTab === 'smsLogs') {
      loadSmsLogs(startDate, endDate, smsCaseNumber, smsLogProcedureStartDate, smsLogProcedureEndDate);
    }
  };

  const handleExportPdf = () => {
    if (reportLoading || !reportData) {
      alert('Report is still loading. Please try again in a moment.');
      return;
    }

    if (activeTab !== 'report') {
      setActiveTab('report');
      setTimeout(() => window.print(), 250);
      return;
    }

    window.print();
  };

  useEffect(() => {
    loadStatistics(null, null);
  }, [loadStatistics]);

  useEffect(() => {
    if (activeTab === 'smsLogs') {
      loadSmsLogs(appliedStartDate, appliedEndDate, smsCaseNumber, smsLogProcedureStartDate, smsLogProcedureEndDate);
    }
  }, [activeTab, appliedStartDate, appliedEndDate, loadSmsLogs, smsLogProcedureStartDate, smsLogProcedureEndDate]);

  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setAppliedStartDate(null);
    setAppliedEndDate(null);
    loadStatistics(null, null);
  };

  return (
    <Page>
      <PageHeaderRow>
        <PageTitle>Statistics</PageTitle>
        <HeaderActions className="no-print">
          <ExportButton onClick={handleExportPdf}>Export PDF</ExportButton>
        </HeaderActions>
      </PageHeaderRow>

      <FiltersRow>
        <DateFilter>
          <Label>Start date</Label>
          <DatePicker value={startDate} onChange={handleStartDateChange} placeholder="mm/dd/yyyy" />
        </DateFilter>
        <DateFilter>
          <Label>End date</Label>
          <DatePicker value={endDate} onChange={handleEndDateChange} placeholder="mm/dd/yyyy" />
        </DateFilter>
        <FiltersActions>
          <SearchButton type="button" onClick={handleSearchDates}>
            Search
          </SearchButton>
          {(startDate || endDate || appliedStartDate || appliedEndDate) && (
            <ClearButton type="button" onClick={clearDates}>Clear</ClearButton>
          )}
        </FiltersActions>
      </FiltersRow>

      <RangeSummary className="no-print">
        <RangeMetric>
          <RangeMetricLabel>Selected range</RangeMetricLabel>
          <RangeMetricValue>{formatSelectedRangeLabel(appliedStartDate, appliedEndDate)}</RangeMetricValue>
        </RangeMetric>
        <RangeMetric>
          <RangeMetricLabel>Cases created</RangeMetricLabel>
          <RangeMetricValue>{reportData?.overview?.casesCreated ?? '—'}</RangeMetricValue>
        </RangeMetric>
      </RangeSummary>

      <TabBar>
        {TABS.map(t => (
          <Tab key={t.id} $active={activeTab === t.id} onClick={() => setActiveTab(t.id)}>
            {t.label}
          </Tab>
        ))}
      </TabBar>

      {activeTab === 'video' && (
        videoLoading ? <Loading>Loading…</Loading> :
        videoError   ? <ErrorMsg>Failed to load: {videoError}</ErrorMsg> :
        <VideoTab data={videoData} />
      )}

      {activeTab === 'avatars' && (
        avatarLoading ? <Loading>Loading…</Loading> :
        avatarError   ? <ErrorMsg>Failed to load: {avatarError}</ErrorMsg> :
        <AvatarTab data={avatarData} />
      )}

      {activeTab === 'dropoff' && (
        (videoLoading || avatarLoading) ? <Loading>Loading…</Loading> :
        (videoError || avatarError)     ? <ErrorMsg>Failed to load data</ErrorMsg> :
        <DropoffTab videoData={videoData} avatarData={avatarData} />
      )}

      {activeTab === 'satisfaction' && (
        reportLoading ? <Loading>Loading…</Loading> :
        reportError   ? <ErrorMsg>Failed to load: {reportError}</ErrorMsg> :
        <SatisfactionTab data={reportData} />
      )}

      {activeTab === 'report' && (
        reportLoading ? <Loading>Loading…</Loading> :
        reportError   ? <ErrorMsg>Failed to load: {reportError}</ErrorMsg> :
        <ReportTab data={reportData} />
      )}

      {activeTab === 'smsLogs' && (
        smsLogsLoading ? <Loading>Loading SMS logs…</Loading> :
        smsLogsError ? <ErrorMsg>Failed to load: {smsLogsError}</ErrorMsg> :
        <SmsLogsTab
          logs={smsLogs}
          caseNumber={smsCaseNumber}
          setCaseNumber={setSmsCaseNumber}
          procedureStartDate={smsLogProcedureStartDate}
          procedureEndDate={smsLogProcedureEndDate}
          setProcedureStartDate={setSmsLogProcedureStartDate}
          setProcedureEndDate={setSmsLogProcedureEndDate}
          onSearch={() => loadSmsLogs(
            appliedStartDate,
            appliedEndDate,
            smsCaseNumber,
            smsLogProcedureStartDate,
            smsLogProcedureEndDate
          )}
          onClear={() => {
            setSmsCaseNumber('');
            setSmsLogProcedureStartDate('');
            setSmsLogProcedureEndDate('');
            loadSmsLogs(appliedStartDate, appliedEndDate, '', '', '');
          }}
        />
      )}
    </Page>
  );
}

function SmsLogsTab({
  logs,
  caseNumber,
  setCaseNumber,
  procedureStartDate,
  procedureEndDate,
  setProcedureStartDate,
  setProcedureEndDate,
  onSearch,
  onClear,
}) {
  const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <Section>
      <SectionHeader><h2>SMS History</h2></SectionHeader>
      <FiltersRow>
        <DateFilter>
          <Label>Case number</Label>
          <input
            value={caseNumber}
            onChange={(event) => setCaseNumber(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') onSearch(); }}
            placeholder="Search case number"
          />
        </DateFilter>
        <SmsFilterField>
          <Label>Procedure date from</Label>
          <SmsFilterInput type="date" value={procedureStartDate} onChange={(event) => setProcedureStartDate(event.target.value)} />
        </SmsFilterField>
        <SmsFilterField>
          <Label>Procedure date to</Label>
          <SmsFilterInput type="date" value={procedureEndDate} onChange={(event) => setProcedureEndDate(event.target.value)} />
        </SmsFilterField>
        <SearchButton type="button" onClick={onSearch}>Search</SearchButton>
        {(caseNumber || procedureStartDate || procedureEndDate) && (
          <ClearButton type="button" onClick={onClear}>Clear</ClearButton>
        )}
      </FiltersRow>
      <p style={{ color: '#666', fontSize: 12, marginBottom: 16 }}>
        Showing up to 500 SMS event records. Older events may not have per-message open timestamps.
      </p>
      {logs.length ? (
        <TableScroll>
          <Table>
            <thead>
              <tr><Th>Case Number</Th><Th>Procedure Date</Th><Th>SMS Type</Th><Th>Status</Th><Th>Sent / Attempted</Th><Th>Opened</Th></tr>
            </thead>
            <tbody>
              {logs.map((row) => (
                <Tr key={row.id}>
                  <Td>{row.caseNumber || '—'}</Td>
                  <Td>{row.procedureDate ? new Date(row.procedureDate).toLocaleDateString() : '—'}</Td>
                  <Td>{FACING_LABELS[row.messageType] || row.messageType}</Td>
                  <Td>{row.status}</Td>
                  <Td><Muted>{formatDateTime(row.sentAt || row.loggedAt)}</Muted></Td>
                  <Td>{row.openedAt ? <Check>{formatDateTime(row.openedAt)}</Check> : <Muted>Not recorded</Muted>}</Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      ) : <EmptyState>No SMS records found for this range and case number.</EmptyState>}
    </Section>
  );
}

// ─── VIDEO TAB ────────────────────────────────────────────────────────────────

function VideoTab({ data }) {
  const { totals, byFacingType, byDate = [] } = data;
  const completionBase = totals.videoStarted || totals.smsOpened;
  const completionRate = completionBase
    ? Math.round((totals.videoCompleted / completionBase) * 100) : 0;

  const CARDS = [
    { key: 'casesCreated',        label: 'Cases Created',           color: '#7c3aed', desc: 'Cases created in the selected date range' },
    { key: 'smsSent',             label: 'SMS Sent',                 color: '#5b8def', desc: 'Successfully sent in the selected date range' },
    { key: 'reminderSmsSent',     label: 'Reminder SMS Sent',        color: '#587feb', desc: 'Unopened first-sending follow-up reminders sent in range' },
    { key: 'smsOpened',           label: 'SMS Opens',                color: '#84a4fc', desc: 'Unique cases that entered the patient flow in the selected date range' },
    { key: 'smsOpenRate',         label: 'SMS Open Rate',            color: '#6c9bfa', desc: `${totals.smsOpenedCases ?? 0} of ${totals.smsSentCases ?? 0} cases sent SMS in range opened` },
    { key: 'avatarDone',          label: 'Avatar Selected',         color: '#6c9bfa', desc: 'Chose an avatar' },
    { key: 'questionnaireDone',   label: 'Questionnaire Answered',  color: '#5a8af8', desc: 'Answered questions' },
    { key: 'videoStarted',        label: 'Video Started',           color: '#4c6ef5', desc: 'Pressed play' },
    { key: 'videoCompleted',      label: 'Watched >= 75%',          color: '#27ae60', desc: 'Watched at least 75% of the video' },
    { key: 'startedNotFinished',  label: 'Started < 75%',           color: '#e67e22', desc: 'Started watching but stopped before 75%' },
    { key: 'satisfactionAnswered', label: 'Satisfaction Answered',  color: '#9b59b6', desc: 'Answered at least one satisfaction question' },
    { key: 'noProgressAfterOpen', label: 'No Progress After Open',  color: '#f02a4c', desc: 'Took no action' },
  ];

  const formatDateLabel = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      <SectionHeader>
        <h2>Patient Flow Overview</h2>
        <CompletionBadge $rate={completionRate}>{completionRate}% completion rate</CompletionBadge>
      </SectionHeader>

      <CardsGrid>
        {CARDS.map(({ key, label, color, desc }) => (
          <StatCard key={key} $color={color}>
            <CardValue>{key === 'smsOpenRate'
              ? `${totals.smsOpenRate ?? 0}%`
              : totals[key] ?? 0}</CardValue>
            <CardLabel>{label}</CardLabel>
            <CardDesc>{desc}</CardDesc>
          </StatCard>
        ))}
      </CardsGrid>

      <Section>
        <SectionTitle>Engagement Funnel</SectionTitle>
        <FunnelWrapper>
          {[
            { label: 'SMS Sent',              value: totals.smsSent,               color: '#5b8def' },
            { label: 'Reminder SMS Sent',      value: totals.reminderSmsSent,       color: '#587feb', denominator: totals.smsSent },
            { label: 'Opened SMS',             value: totals.smsOpenedCases,        color: '#84a4fc', denominator: totals.smsSentCases },
            { label: 'Avatar Selected',        value: totals.avatarDone,        color: '#6c9bfa' },
            { label: 'Questionnaire Answered', value: totals.questionnaireDone, color: '#5a8af8' },
            { label: 'Watched >= 75%',         value: totals.videoCompleted,    color: '#27ae60' },
          ].map(({ label, value, color, denominator }) => {
            const base = denominator ?? totals.smsOpenedCases;
            const pct = base ? Math.round((value / base) * 100) : 0;
            return (
              <FunnelRow key={label}>
                <FunnelLabel>{label}</FunnelLabel>
                <FunnelBarWrapper>
                  <FunnelBar $pct={pct} $color={color} />
                </FunnelBarWrapper>
                <FunnelNumbers>{value} <Muted>({pct}%)</Muted></FunnelNumbers>
              </FunnelRow>
            );
          })}
        </FunnelWrapper>
      </Section>

      {byFacingType?.length > 0 && (
        <Section>
          <SectionTitle>Breakdown by Flow Type</SectionTitle>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Flow</Th><Th>SMS Sent</Th><Th>Reminders Sent</Th><Th>Opened</Th><Th>Avatar</Th><Th>Questionnaire</Th>
                  <Th>Video Started</Th><Th>Video Completed</Th><Th>Not Finished</Th><Th>No Progress</Th><Th>%</Th>
                </tr>
              </thead>
              <tbody>
                {byFacingType.map(row => {
                  const pct = row.smsOpened > 0 ? Math.round((row.videoCompleted / row.smsOpened) * 100) : 0;
                  return (
                    <Tr key={row.facingType}>
                      <Td><FlowBadge>{FACING_LABELS[row.facingType] || row.facingType}</FlowBadge></Td>
                      <Td>{row.smsSent}</Td>
                      <Td>{row.reminderSmsSent}</Td>
                      <Td>{row.smsOpenedMessages}</Td>
                      <Td>{row.avatarDone}</Td>
                      <Td>{row.questionnaireDone}</Td>
                      <Td><Blue>{row.videoStarted}</Blue></Td>
                      <Td><Green>{row.videoCompleted}</Green></Td>
                      <Td><Orange>{row.startedNotFinished}</Orange></Td>
                      <Td><Red>{row.noProgressAfterOpen}</Red></Td>
                      <Td>
                        <PctBar $pct={pct}><span>{pct}%</span></PctBar>
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScroll>
        </Section>
      )}

      {byDate.length > 0 && (
        <Section>
          <SectionTitle>{'Watched >= 75% by Date (last 30 days)'}</SectionTitle>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Date</Th>
                  <Th>{'Watched >= 75%'}</Th>
                </tr>
              </thead>
              <tbody>
                {byDate.map(row => (
                  <Tr key={row.date}>
                    <Td>{formatDateLabel(row.date)}</Td>
                    <Td><Green>{row.videoCompleted}</Green></Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </Section>
      )}

    </>
  );
}

// ─── AVATAR TAB ───────────────────────────────────────────────────────────────

function AvatarTab({ data }) {
  const { totals, byGender, byAge, byEthnicity, byGenderEthnicity, recent } = data;

  return (
    <>
      <SectionHeader><h2>Avatar Storage Overview</h2></SectionHeader>

      <CardsGrid>
        {[
          { label: 'Avatars Created',      value: totals.avatarsCreated ?? totals.avatarsStored, color: '#84a4fc', desc: 'Users who created an avatar' },
          { label: 'Avatars Stored',       value: totals.avatarsStored,       color: '#84a4fc', desc: 'Total avatars saved in DB' },
          { label: 'Cases Opened',         value: totals.casesOpened ?? totals.casesOpenedSms, color: '#6c9bfa', desc: 'GI/GI2 cases that opened the link' },
          { label: 'Missing Avatar',       value: totals.casesMissingAvatar,  color: '#f02a4c', desc: 'Opened but no avatar saved' },
          { label: 'Capture Rate',         value: `${totals.avatarCaptureRate}%`, color: '#27ae60', desc: 'Avatar stored vs SMS opened' },
        ].map(({ label, value, color, desc }) => (
          <StatCard key={label} $color={color}>
            <CardValue>{value}</CardValue>
            <CardLabel>{label}</CardLabel>
            <CardDesc>{desc}</CardDesc>
          </StatCard>
        ))}
      </CardsGrid>

      <BreakdownGrid>
        <Section>
          <SectionTitle>By Gender</SectionTitle>
          {byGender.map(r => <BarRow key={r.gender} label={r.gender} value={r.count} total={totals.avatarsStored} color="#84a4fc" />)}
        </Section>

        <Section>
          <SectionTitle>By Ethnicity</SectionTitle>
          {byEthnicity.map(r => <BarRow key={r.ethnicity} label={r.ethnicity} value={r.count} total={totals.avatarsStored} color="#6c9bfa" />)}
        </Section>

        <Section>
          <SectionTitle>By Age Group</SectionTitle>
          {byAge.map(r => <BarRow key={r.age} label={r.age} value={r.count} total={totals.avatarsStored} color="#5a8af8" />)}
        </Section>

        <Section>
          <SectionTitle>Gender × Ethnicity</SectionTitle>
          {byGenderEthnicity.map(r => (
            <BarRow
              key={`${r.gender}-${r.ethnicity}`}
              label={`${r.gender} / ${r.ethnicity}`}
              value={r.count}
              total={totals.avatarsStored}
              color="#27ae60"
            />
          ))}
        </Section>
      </BreakdownGrid>

      {recent?.length > 0 && (
        <Section>
          <SectionTitle>Recent Avatars (last 50)</SectionTitle>
          <TableScroll>
            <Table>
              <thead>
                <tr><Th>Gender</Th><Th>Age</Th><Th>Ethnicity</Th><Th>Saved At</Th></tr>
              </thead>
              <tbody>
                {recent.map(r => (
                  <Tr key={r.caseId}>
                    <Td>{r.gender}</Td>
                    <Td>{r.age}</Td>
                    <Td>{r.ethnicity}</Td>
                    <Td><Muted>{r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}</Muted></Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        </Section>
      )}
    </>
  );
}

function formatSelectedRangeLabel(startDate, endDate) {
  const formatDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const startLabel = formatDate(startDate);
  const endLabel = formatDate(endDate);

  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  if (startLabel) return startLabel;
  if (endLabel) return endLabel;
  return 'All available dates';
}

function BarRow({ label, value, total, color }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <FunnelRow>
      <FunnelLabel style={{ textTransform: 'capitalize' }}>{label}</FunnelLabel>
      <FunnelBarWrapper>
        <FunnelBar $pct={pct} $color={color} />
      </FunnelBarWrapper>
      <FunnelNumbers>{value} <Muted>({pct}%)</Muted></FunnelNumbers>
    </FunnelRow>
  );
}

// ─── DROP-OFF TAB ─────────────────────────────────────────────────────────────

function DropoffTab({ videoData, avatarData }) {
  const v = videoData.totals;
  const a = avatarData.totals;
  const [smsSentStartDate, setSmsSentStartDate] = useState('');
  const [smsSentEndDate, setSmsSentEndDate] = useState('');
  const [procedureStartDate, setProcedureStartDate] = useState('');
  const [procedureEndDate, setProcedureEndDate] = useState('');
  const [smsCaseNumber, setSmsCaseNumber] = useState('');
  const [smsCaseRows, setSmsCaseRows] = useState([]);
  const [smsCasesLoading, setSmsCasesLoading] = useState(false);
  const [smsCasesError, setSmsCasesError] = useState(null);

  const fetchSmsCaseFollowups = useCallback(async (sentStart, sentEnd, caseNumber, procedureStart, procedureEnd) => {
    setSmsCasesLoading(true);
    setSmsCasesError(null);
    const params = {};
    if (sentStart) params.smsSentStartDate = sentStart;
    if (sentEnd) params.smsSentEndDate = sentEnd;
    if (caseNumber?.trim()) params.caseNumber = caseNumber.trim();
    if (procedureStart) params.procedureStartDate = procedureStart;
    if (procedureEnd) params.procedureEndDate = procedureEnd;
    try {
      const { data } = await axios.get('/api/admin/sms-case-followups', { params });
      setSmsCaseRows(data.data || []);
    } catch (error) {
      setSmsCasesError(error.message);
    } finally {
      setSmsCasesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSmsCaseFollowups('', '', '', '', '');
  }, [fetchSmsCaseFollowups]);

  const smsCaseTotals = smsCaseRows.reduce((totals, row) => {
    totals.cases += 1;
    totals.sent += Number(Boolean(row.firstSmsSentAt)) + Number(row.reminderSmsSent || 0) + Number(Boolean(row.secondSmsSentAt));
    totals.opened += Number(Boolean(row.firstSmsOpenedAt)) + Number(row.reminderSmsOpened || 0) + Number(Boolean(row.secondSmsOpenedAt));
    totals.remindersSent += Number(row.reminderSmsSent || 0);
    totals.remindersOpened += Number(row.reminderSmsOpened || 0);
    return totals;
  }, { cases: 0, sent: 0, opened: 0, remindersSent: 0, remindersOpened: 0 });

  const totals = {
    casesCreated: v.casesCreated ?? 0,
    smsSent: v.smsSent ?? 0,
    reminderSmsSent: v.reminderSmsSent ?? 0,
    smsSentCases: v.smsSentCasesInCohort ?? 0,
    reminderSmsSentCases: v.reminderSmsSentCasesInCohort ?? 0,
    smsOpened: v.smsOpenedCasesInCohort ?? 0,
    avatarDone: v.avatarDone ?? 0,
    questionnaireDone: v.questionnaireDone ?? 0,
    videoStarted: v.videoStarted ?? 0,
    videoCompleted: v.videoCompleted ?? 0,
    startedNotFinished: v.startedNotFinished ?? Math.max((v.videoStarted ?? 0) - (v.videoCompleted ?? 0), 0),
    noProgressAfterOpen: v.noProgressAfterOpen ?? 0,
  };

  const steps = [
    { label: 'Cases Created',                value: totals.casesCreated,         prev: null, denominator: totals.casesCreated },
    { label: 'Cases Sent SMS Successfully',  value: totals.smsSentCases,         prev: totals.casesCreated, denominator: totals.casesCreated },
    { label: 'Cases Opened SMS',             value: totals.smsOpened,            prev: totals.smsSentCases, denominator: totals.smsSentCases },
    { label: 'Cases Sent Reminder SMS',      value: totals.reminderSmsSentCases, prev: null, denominator: totals.smsSentCases },
    { label: 'Selected avatar',         value: totals.avatarDone,         prev: totals.smsOpened },
    { label: 'Answered questionnaire',  value: totals.questionnaireDone,  prev: totals.avatarDone },
    { label: 'Started video',           value: totals.videoStarted,       prev: totals.questionnaireDone },
    { label: 'Completed video',         value: totals.videoCompleted,     prev: totals.videoStarted },
  ];

  const formatDateTimes = (values) => Array.isArray(values) && values.length
    ? values.map((value) => new Date(value).toLocaleString()).join('\n')
    : '—';

  const issues = [];
  if (a.casesMissingAvatar > 0) {
    issues.push({
      severity: 'high',
      title: `${a.casesMissingAvatar} patient(s) have no avatar in DB`,
      detail: 'Avatar not saved to database despite opening SMS. Likely a failed /api/avatar call.',
    });
  }
  if (totals.noProgressAfterOpen > 0) {
    issues.push({
      severity: 'medium',
      title: `${totals.noProgressAfterOpen} patient(s) opened link but took no action`,
      detail: 'Opened the SMS link but never selected an avatar. May indicate UX friction on landing page.',
    });
  }
  if (totals.startedNotFinished > 0) {
    issues.push({
      severity: 'low',
      title: `${totals.startedNotFinished} patient(s) started but never completed the video`,
      detail: 'Pressed play but never finished the video to 75%',
    });
  }
  if (totals.avatarDone > a.avatarsStored) {
    issues.push({
      severity: 'high',
      title: `CasesProgress avatarSelection (${totals.avatarDone}) > Avatars table (${a.avatarsStored})`,
      detail: 'avatarSelection timestamp is set in CasesProgress but no corresponding avatar row exists. postAnalytics call may succeed while /api/avatar call fails.',
    });
  }

  return (
    <>
      <SectionHeader><h2>Funnel Drop-off</h2></SectionHeader>

      <Section>
        <SectionTitle>Step-by-step Drop-off</SectionTitle>
        {steps.map((step, i) => {
          const dropped = step.prev !== null ? step.prev - step.value : 0;
          const dropPct = step.prev > 0 ? Math.round((dropped / step.prev) * 100) : 0;
          const denominator = step.denominator ?? totals.smsOpened;
          const pct = denominator > 0 ? Math.round((step.value / denominator) * 100) : 0;
          return (
            <DropoffRow key={i}>
              <DropoffStep>
                <DropoffNum>{i + 1}</DropoffNum>
                <DropoffInfo>
                  <DropoffLabel>{step.label}</DropoffLabel>
                  {step.prev !== null && dropped > 0 && (
                    <DropoffLost>▼ {dropped} dropped ({dropPct}% of previous step)</DropoffLost>
                  )}
                </DropoffInfo>
              </DropoffStep>
              <DropoffRight>
                <DropoffValue>{step.value}</DropoffValue>
                <DropoffBar>
                  <DropoffFill $pct={pct} $color={pct >= 70 ? '#27ae60' : pct >= 40 ? '#e67e22' : '#f02a4c'} />
                </DropoffBar>
                <DropoffPct><Muted>{pct}% of total</Muted></DropoffPct>
              </DropoffRight>
            </DropoffRow>
          );
        })}
      </Section>

      <Section>
        <SectionTitle>SMS Analytics</SectionTitle>
        <SmsFilters>
          <SmsFilterField>
            <Label>Case number</Label>
            <SmsFilterInput
              value={smsCaseNumber}
              onChange={(event) => setSmsCaseNumber(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') fetchSmsCaseFollowups(smsSentStartDate, smsSentEndDate, smsCaseNumber, procedureStartDate, procedureEndDate);
              }}
              placeholder="Search case number"
            />
          </SmsFilterField>
          <SmsFilterField>
            <Label>SMS sent from</Label>
            <SmsFilterInput type="date" value={smsSentStartDate} onChange={(event) => setSmsSentStartDate(event.target.value)} />
          </SmsFilterField>
          <SmsFilterField>
            <Label>SMS sent to</Label>
            <SmsFilterInput type="date" value={smsSentEndDate} onChange={(event) => setSmsSentEndDate(event.target.value)} />
          </SmsFilterField>
          <SmsFilterField>
            <Label>Procedure date from</Label>
            <SmsFilterInput type="date" value={procedureStartDate} onChange={(event) => setProcedureStartDate(event.target.value)} />
          </SmsFilterField>
          <SmsFilterField>
            <Label>Procedure date to</Label>
            <SmsFilterInput type="date" value={procedureEndDate} onChange={(event) => setProcedureEndDate(event.target.value)} />
          </SmsFilterField>
          <SearchButton
            type="button"
            onClick={() => fetchSmsCaseFollowups(smsSentStartDate, smsSentEndDate, smsCaseNumber, procedureStartDate, procedureEndDate)}
            disabled={smsCasesLoading}
          >
            {smsCasesLoading ? 'Loading…' : 'Search'}
          </SearchButton>
          {(smsCaseNumber || smsSentStartDate || smsSentEndDate || procedureStartDate || procedureEndDate) && (
            <ClearButton
              type="button"
              onClick={() => {
                setSmsCaseNumber('');
                setSmsSentStartDate('');
                setSmsSentEndDate('');
                setProcedureStartDate('');
                setProcedureEndDate('');
                fetchSmsCaseFollowups('', '', '', '', '');
              }}
            >
              Clear
            </ClearButton>
          )}
        </SmsFilters>
        <p style={{ color: '#666', fontSize: 12, marginBottom: 16 }}>
          Reminder counts include unopened first-sending follow-ups only; the second SMS is shown separately. The SMS sent range filters to cases with at least one SMS sent in that period. When older messages lack per-message open times, the recorded flow-open time is shown as a fallback.
        </p>
        {smsCasesError ? <ErrorMsg>Failed to load: {smsCasesError}</ErrorMsg> : smsCaseRows.length > 0 ? (
          <>
          <p style={{ color: '#666', fontSize: 12, marginBottom: 8 }}>Totals for the displayed matches (up to 500 cases)</p>
          <CardsGrid>
            {[
              { label: 'Cases Matched', value: smsCaseTotals.cases },
              { label: 'SMS Sent', value: smsCaseTotals.sent },
              { label: 'SMS Opened', value: smsCaseTotals.opened },
              { label: 'Reminder SMS Sent', value: smsCaseTotals.remindersSent },
              { label: 'Reminder SMS Opened', value: smsCaseTotals.remindersOpened },
            ].map(({ label, value }) => (
              <StatCard key={label}>
                <CardValue>{value}</CardValue>
                <CardLabel>{label}</CardLabel>
              </StatCard>
            ))}
          </CardsGrid>
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>Case Number</Th><Th>Procedure Date</Th>
                  <Th>1st SMS Sent</Th><Th>1st SMS Opened</Th>
                  <Th>Reminders Sent / Dates</Th><Th>Reminders Opened / Dates</Th>
                  <Th>2nd SMS Sent</Th><Th>2nd SMS Opened</Th>
                </tr>
              </thead>
              <tbody>
                {smsCaseRows.map((row) => (
                  <Tr key={row.caseId}>
                    <Td>{row.caseNumber || '—'}</Td>
                    <Td>{row.procedureDate ? new Date(row.procedureDate).toLocaleDateString() : '—'}</Td>
                    <Td><Muted>{row.firstSmsSentAt ? new Date(row.firstSmsSentAt).toLocaleString() : '—'}</Muted></Td>
                    <Td>{row.firstSmsOpenedAt ? <Check>{new Date(row.firstSmsOpenedAt).toLocaleString()}</Check> : '—'}</Td>
                    <Td>
                      {row.reminderSmsSent}
                      <EventDateList>{formatDateTimes(row.reminderSmsSentAt)}</EventDateList>
                    </Td>
                    <Td>
                      {row.reminderSmsOpened}
                      <EventDateList>{formatDateTimes(row.reminderSmsOpenedAt)}</EventDateList>
                    </Td>
                    <Td><Muted>{row.secondSmsSentAt ? new Date(row.secondSmsSentAt).toLocaleString() : '—'}</Muted></Td>
                    <Td>{row.secondSmsOpenedAt ? <Check>{new Date(row.secondSmsOpenedAt).toLocaleString()}</Check> : '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
          </>
        ) : smsCasesLoading ? <Loading>Loading SMS follow-up logs…</Loading> : <EmptyState>No GI cases match these filters.</EmptyState>}
      </Section>

      {issues.length > 0 && (
        <Section>
          <SectionTitle>Data Issues Detected</SectionTitle>
          {issues.map((issue, i) => (
            <IssueCard key={i} $severity={issue.severity}>
              <IssueSeverity $severity={issue.severity}>
                {issue.severity === 'high' ? '🔴 High' : issue.severity === 'medium' ? '🟡 Medium' : '🟢 Low'}
              </IssueSeverity>
              <IssueTitle>{issue.title}</IssueTitle>
              <IssueDetail>{issue.detail}</IssueDetail>
            </IssueCard>
          ))}
        </Section>
      )}

      {issues.length === 0 && (
        <Section>
          <EmptyState>✅ No data integrity issues detected.</EmptyState>
        </Section>
      )}
    </>
  );
}

function ReportTab({ data }) {
  const {
    range = {},
    overview = {},
    smsFailureReasons = [],
    byFacingType = [],
    medicationQuestions = [],
    satisfactionQuestions = [],
  } = data || {};

  const formatRangeDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const rangeStartLabel = formatRangeDate(range.startDate);
  const rangeEndLabel = formatRangeDate(range.endDate);
  const rangeLabel = rangeStartLabel && rangeEndLabel
    ? `${rangeStartLabel} – ${rangeEndLabel}`
    : rangeStartLabel || rangeEndLabel || 'All available dates';

  const overviewCards = [
    { label: 'Cases Created', value: overview.casesCreated ?? 0, color: '#84a4fc', desc: 'Number of cases created within the selected date range' },
    { label: 'SMS Messages Sent', value: overview.smsSentEvents ?? 0, color: '#6c9bfa', desc: 'Number of successful SMS sends and resends' },
    { label: 'Reminder SMS Sent', value: overview.reminderSmsSentEvents ?? 0, color: '#587feb', desc: 'Successful unopened first-sending follow-up reminders' },
    { label: 'SMS Messages Not Sent', value: overview.smsNotSentEvents ?? overview.smsFailedEvents ?? 0, color: '#f02a4c', desc: 'Number of failed or unsent SMS messages' },
    { label: 'Cases Opened', value: overview.casesOpened ?? 0, color: '#4c6ef5', desc: 'Number of cases that opened the SMS link' },
    { label: 'Watched >= 75%', value: overview.videoWatched ?? 0, color: '#27ae60', desc: 'Number of users who watched at least 75% of the video' },
    { label: 'Started < 75%', value: overview.startedNotFinished ?? 0, color: '#e67e22', desc: 'Number of users who started watching but stopped before 75%' },
    { label: 'Avatars Created', value: overview.avatarsCreated ?? 0, color: '#8b5cf6', desc: 'Number of users who created an avatar' },
    { label: 'Avatar Capture Rate', value: `${overview.avatarCaptureRate ?? 0}%`, color: '#10b981', desc: 'Avatar creation compared with cases created' },
  ];

  const formatPercent = (value) => `${Number.isFinite(value) ? value : 0}%`;

  const conversionCards = [
    { label: 'SMS sent → Video watched', value: formatPercent(overview.sentMessageWatchRate), color: '#27ae60', desc: 'How many sent messages eventually produced a video view' },
    { label: 'Opened + Questionnaire → Video watched', value: formatPercent(overview.openQuestionnaireWatchRate), color: '#4c6ef5', desc: 'Conversion among users already engaged' },
    { label: 'Questionnaires completed', value: overview.questionnairesCompleted ?? 0, color: '#5a8af8', desc: 'Completed questionnaires in range' },
  ];

  const smsFailedEvents = overview.smsNotSentEvents ?? overview.smsFailedEvents ?? 0;
  const smsFailedCases = overview.smsNotSentCases ?? overview.smsFailedCases ?? 0;
  const smsFailureRate = (overview.smsSentEvents ?? 0) + smsFailedEvents > 0
    ? Math.round((smsFailedEvents / ((overview.smsSentEvents ?? 0) + smsFailedEvents)) * 100)
    : 0;

  const smsFailureCards = [
    { label: 'Failed SMS Events', value: smsFailedEvents, color: '#f02a4c', desc: 'Messages that did not send successfully' },
    { label: 'Failed SMS Cases', value: smsFailedCases, color: '#e67e22', desc: 'Distinct cases that experienced an SMS failure' },
    { label: 'Failure Rate', value: `${smsFailureRate}%`, color: '#b4233d', desc: 'Failed messages out of all SMS send attempts' },
  ];

  return (
    <>
      <SectionHeader>
        <h2>Executive Report</h2>
      </SectionHeader>
      <ReportRangeNote>
        Showing statistics for <strong>{rangeLabel}</strong>.
      </ReportRangeNote>

      <CardsGrid>
        {overviewCards.map(({ label, value, color, desc }) => (
          <StatCard key={label} $color={color}>
            <CardValue>{value}</CardValue>
            <CardLabel>{label}</CardLabel>
            <CardDesc>{desc}</CardDesc>
          </StatCard>
        ))}
      </CardsGrid>

      <CardsGrid>
        {conversionCards.map(({ label, value, color, desc }) => (
          <StatCard key={label} $color={color}>
            <CardValue>{value}</CardValue>
            <CardLabel>{label}</CardLabel>
            <CardDesc>{desc}</CardDesc>
          </StatCard>
        ))}
      </CardsGrid>

      <Section>
        <SectionHeader>
          <h2>SMS Send Failures</h2>
        </SectionHeader>
        <CardsGrid>
          {smsFailureCards.map(({ label, value, color, desc }) => (
            <StatCard key={label} $color={color}>
              <CardValue>{value}</CardValue>
              <CardLabel>{label}</CardLabel>
              <CardDesc>{desc}</CardDesc>
            </StatCard>
          ))}
        </CardsGrid>
        <SectionTitle>Failure Reasons</SectionTitle>
        {smsFailureReasons.length > 0 ? smsFailureReasons.map((row) => (
          <BarRow key={row.reason} label={row.reason} value={row.count} total={smsFailedEvents} color="#f02a4c" />
        )) : <EmptyState>No SMS failures found for the selected range.</EmptyState>}
      </Section>

      <Section>
        <SectionTitle>Flow Breakdown</SectionTitle>
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>Flow</Th>
                <Th>SMS</Th>
                <Th>Avatar</Th>
                <Th>Questionnaire</Th>
                <Th>Video Started</Th>
                <Th>Video Watched</Th>
                <Th>Not Finished</Th>
                <Th>No Progress</Th>
              </tr>
            </thead>
            <tbody>
              {byFacingType.map(row => (
                <Tr key={row.facingType}>
                  <Td><FlowBadge>{FACING_LABELS[row.facingType] || row.facingType}</FlowBadge></Td>
                  <Td>{row.smsOpened}</Td>
                  <Td>{row.avatarDone}</Td>
                  <Td>{row.questionnaireDone}</Td>
                  <Td>{row.videoStarted}</Td>
                  <Td><Green>{row.videoWatched}</Green></Td>
                  <Td><Orange>{row.startedNotFinished}</Orange></Td>
                  <Td><Red>{row.noProgressAfterOpen}</Red></Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      </Section>

      <BreakdownGrid>
        <Section>
          <SectionTitle>Medication Question Statistics</SectionTitle>
          {medicationQuestions.map((row) => (
            <QuestionStat key={row.key}>
              <QuestionLabel>{row.label}</QuestionLabel>
              <QuestionCounts>Yes: <strong>{row.yes}</strong> · No: <strong>{row.no}</strong> · Unknown: <strong>{row.unknown}</strong></QuestionCounts>
            </QuestionStat>
          ))}
        </Section>

        <Section>
          <SectionTitle>Satisfaction Question Statistics</SectionTitle>
          {satisfactionQuestions.length > 0 ? satisfactionQuestions.map((row) => (
            <QuestionStat key={row.key}>
              <QuestionLabel>{row.label}</QuestionLabel>
              <QuestionCounts>Responses: <strong>{row.responses}</strong> · Yes: <strong>{row.yes}</strong> · No: <strong>{row.no}</strong></QuestionCounts>
            </QuestionStat>
          )) : <EmptyState>No satisfaction answers recorded in the selected range.</EmptyState>}
        </Section>
      </BreakdownGrid>
    </>
  );
}

// ─── SATISFACTION TAB ─────────────────────────────────────────────────────────

function SatisfactionTab({ data }) {
  const { overview = {}, satisfactionQuestions = [] } = data || {};
  const totalResponses = satisfactionQuestions.reduce((sum, q) => sum + (q.responses || 0), 0);
  const responseRate = overview.videoWatched > 0
    ? Math.round((totalResponses / overview.videoWatched) * 100)
    : 0;

  const formatLabel = (key) => {
    const map = {
      'video-helpful': 'Did you find the video helpful?',
      'allay': 'Did the video allay your concerns?',
      'helped-understand': 'Did the video help you understand preparation?',
      'recommend-others': 'Would you recommend the video to others?',
      'reduce-worries': 'Did the video reduce your worries?',
      'recommend-other-parents': 'Would you recommend to other parents?',
    };
    return map[key] || key;
  };

  return (
    <>
      <SectionHeader>
        <h2>Satisfaction Survey</h2>
      </SectionHeader>

      <CardsGrid>
        <StatCard $color="#9b59b6">
          <CardValue>{totalResponses}</CardValue>
          <CardLabel>Total Responses</CardLabel>
          <CardDesc>Patients who answered at least one question</CardDesc>
        </StatCard>
        <StatCard $color="#8e44ad">
          <CardValue>{responseRate}%</CardValue>
          <CardLabel>Response Rate</CardLabel>
          <CardDesc>Responses out of patients who watched ≥75% of the video</CardDesc>
        </StatCard>
        <StatCard $color="#27ae60">
          <CardValue>{overview.videoWatched ?? 0}</CardValue>
          <CardLabel>Videos Watched ≥75%</CardLabel>
          <CardDesc>Eligible patients for satisfaction survey</CardDesc>
        </StatCard>
      </CardsGrid>

      <Section>
        <SectionTitle>Question Breakdown</SectionTitle>
        {satisfactionQuestions.length > 0 ? satisfactionQuestions.map((row) => {
          const total = row.responses || 1;
          const yesPct = Math.round(((row.yes || 0) / total) * 100);
          const noPct = Math.round(((row.no || 0) / total) * 100);
          const unknownPct = 100 - yesPct - noPct;
          return (
            <QuestionStat key={row.key}>
              <QuestionLabel>{formatLabel(row.label || row.key)}</QuestionLabel>
              <QuestionCounts>
                Responses: <strong>{row.responses || 0}</strong> ·
                Yes: <strong>{row.yes || 0}</strong> ·
                No: <strong>{row.no || 0}</strong>
                {(row.unknown || 0) > 0 && <> · Unknown: <strong>{row.unknown}</strong></>}
              </QuestionCounts>
              <SatisfactionBar>
                {yesPct > 0 && <SatisfactionBarSegment $pct={yesPct} $color="#27ae60" />}
                {noPct > 0 && <SatisfactionBarSegment $pct={noPct} $color="#e67e22" />}
                {unknownPct > 0 && <SatisfactionBarSegment $pct={unknownPct} $color="#bdc3c7" />}
              </SatisfactionBar>
              <SatisfactionLegend>
                <span><SatisfactionDot $color="#27ae60" /> Yes {yesPct}%</span>
                <span><SatisfactionDot $color="#e67e22" /> No {noPct}%</span>
                {unknownPct > 0 && <span><SatisfactionDot $color="#bdc3c7" /> Unknown {unknownPct}%</span>}
              </SatisfactionLegend>
            </QuestionStat>
          );
        }) : <EmptyState>No satisfaction answers recorded in the selected range.</EmptyState>}
      </Section>
    </>
  );
}

// ─── STYLED COMPONENTS ────────────────────────────────────────────────────────

const Page = styled.div`font-family: 'Poppins', sans-serif; color: #1a1a1a;`;
const PageTitle = styled.h1`font-size: 1.8rem; color: #333; margin: 0;`;
const PageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;
const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  @media print {
    display: none;
  }
`;

const FiltersActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
  padding-bottom: 0.1rem;
`;
const ExportButton = styled.button`
  padding: 0.55rem 1rem;
  border: none;
  border-radius: 999px;
  background: #f02a4c;
  color: white;
  font-weight: 700;
  cursor: pointer;
  &:hover { background: #d12442; }
`;
const FiltersRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: flex-end;
  margin-bottom: 1.5rem;
  @media print {
    display: none;
  }
`;

const RangeSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 1.5rem;
  align-items: center;
  justify-content: flex-start;
  margin: -0.5rem 0 1.25rem;
  padding: 0.85rem 1rem;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  background: #fafafa;
  color: #444;
  font-size: 0.92rem;

  strong {
    color: #111;
  }
`;

const RangeMetric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 180px;
  padding: 0.35rem 0.6rem;
  border: 1px solid #e4e4e4;
  border-radius: 12px;
  background: white;
`;

const RangeMetricLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #777;
`;

const RangeMetricValue = styled.div`
  font-size: 0.96rem;
  font-weight: 700;
  color: #222;
`;

const DateFilter = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 180px;
  gap: 0.4rem;
`;

const SmsFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const SmsFilterField = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 150px;
  gap: 0.4rem;
`;

const SmsFilterInput = styled.input`
  min-height: 36px;
  padding: 0.4rem 0.7rem;
  border: 1px solid #d7dce5;
  border-radius: 8px;
  background: white;
  color: #333;
  font: inherit;
  font-size: 0.85rem;
  &:focus { outline: 2px solid #84a4fc; border-color: transparent; }
`;

const EventDateList = styled.div`
  margin-top: 0.25rem;
  color: #777;
  font-size: 0.7rem;
  line-height: 1.35;
  white-space: pre-line;
`;

const Label = styled.label`
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
`;

const ClearButton = styled.button`
  align-self: flex-end;
  padding: 0.45rem 0.9rem;
  border: none;
  border-radius: 999px;
  background: #f4f4f4;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s;
  &:hover { background: #e8e8e8; }
`;

const SearchButton = styled.button`
  align-self: flex-end;
  padding: 0.45rem 1rem;
  border: none;
  border-radius: 999px;
  background: #f02a4c;
  color: white;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s, transform 0.2s;

  &:hover {
    background: #d12442;
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.98);
  }
`;

const Loading = styled.div`padding: 3rem; color: #999;`;
const ErrorMsg = styled.div`padding: 3rem; color: #f02a4c;`;
const EmptyState = styled.div`padding: 1rem; color: #999; font-size: 0.9rem;`;

const TabBar = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #f0f0f0;
  @media print {
    display: none;
  }
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: none;
  font-size: 0.95rem;
  font-weight: ${p => p.$active ? '700' : '500'};
  color: ${p => p.$active ? '#f02a4c' : '#666'};
  border-bottom: 2px solid ${p => p.$active ? '#f02a4c' : 'transparent'};
  margin-bottom: -2px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { color: #f02a4c; }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  h2 { margin: 0; font-size: 1.2rem; color: #333; }
`;

const CompletionBadge = styled.div`
  padding: 0.35rem 0.9rem;
  border-radius: 20px;
  background: ${p => p.$rate >= 70 ? '#e8f8f0' : p.$rate >= 40 ? '#fff8e8' : '#fff0f3'};
  color: ${p => p.$rate >= 70 ? '#27ae60' : p.$rate >= 40 ? '#e67e22' : '#f02a4c'};
  font-weight: 600;
  font-size: 0.9rem;
`;

const ReportRangeNote = styled.div`
  margin: -0.75rem 0 1.25rem;
  font-size: 0.92rem;
  color: #666;

  strong {
    color: #333;
  }
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  border-left: 4px solid ${p => p.$color};
`;

const CardValue = styled.div`font-size: 2rem; font-weight: 700; color: #1a1a1a;`;
const CardLabel = styled.div`font-size: 0.85rem; font-weight: 600; color: #444; margin-top: 0.25rem;`;
const CardDesc = styled.div`font-size: 0.75rem; color: #999; margin-top: 0.25rem;`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  margin-bottom: 1.5rem;
  @media print {
    box-shadow: none;
    break-inside: avoid;
    page-break-inside: avoid;
  }
`;

const SectionTitle = styled.h2`margin: 0 0 1.25rem; font-size: 1.05rem; color: #333;`;

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FunnelWrapper = styled.div`display: flex; flex-direction: column; gap: 0.75rem;`;
const FunnelRow = styled.div`display: grid; grid-template-columns: 200px 1fr 110px; align-items: center; gap: 1rem;`;
const FunnelLabel = styled.div`font-size: 0.85rem; font-weight: 500; color: #444;`;
const FunnelBarWrapper = styled.div`background: #f0f0f0; border-radius: 6px; height: 20px; overflow: hidden;`;
const FunnelBar = styled.div`height: 100%; width: ${p => p.$pct}%; background: ${p => p.$color}; border-radius: 6px; transition: width 0.4s ease;`;
const FunnelNumbers = styled.div`font-size: 0.85rem; font-weight: 600; color: #333;`;
const Muted = styled.span`color: #999; font-weight: 400;`;

const TableScroll = styled.div`overflow-x: auto;`;
const Table = styled.table`width: 100%; border-collapse: collapse; font-size: 0.85rem;`;
const Th = styled.th`text-align: left; padding: 0.6rem 0.75rem; color: #999; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; border-bottom: 2px solid #f0f0f0;`;
const Tr = styled.tr`&:hover { background: #fafafa; } border-bottom: 1px solid #f5f5f5;`;
const Td = styled.td`padding: 0.65rem 0.75rem; color: #333;`;

const FlowBadge = styled.span`background: #f0f4ff; color: #84a4fc; padding: 0.2rem 0.6rem; border-radius: 10px; font-size: 0.78rem; font-weight: 500;`;
const Blue = styled.span`color: #4c6ef5; font-weight: 600;`;
const Green = styled.span`color: #27ae60; font-weight: 600;`;
const Orange = styled.span`color: #e67e22; font-weight: 600;`;
const Red = styled.span`color: #f02a4c; font-weight: 600;`;

const PctBar = styled.div`
  display: flex; align-items: center; gap: 0.5rem;
  &::before {
    content: '';
    display: inline-block;
    width: ${p => p.$pct}px; max-width: 80px; height: 8px;
    background: ${p => p.$pct >= 70 ? '#27ae60' : p.$pct >= 40 ? '#e67e22' : '#f02a4c'};
    border-radius: 4px;
  }
  span { font-size: 0.8rem; font-weight: 600; color: #444; }
`;

const Check = styled.span`color: #27ae60; font-weight: 700;`;
const Cross = styled.span`color: #ddd;`;

const QuestionStat = styled.div`
  padding: 0.85rem 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-child { border-bottom: none; }
`;

const QuestionLabel = styled.div`
  font-size: 0.92rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.2rem;
`;

const QuestionCounts = styled.div`
  font-size: 0.82rem;
  color: #666;
`;

const SatisfactionBar = styled.div`
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 0.75rem;
  background: #f3f4f6;
`;

const SatisfactionBarSegment = styled.div`
  width: ${p => p.$pct || 0}%;
  background: ${p => p.$color};
`;

const SatisfactionLegend = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
  font-size: 0.75rem;
  color: #666;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }
`;

const SatisfactionDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.$color};
`;

const DropoffRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid #f5f5f5;
  &:last-child { border-bottom: none; }
`;

const DropoffStep = styled.div`display: flex; align-items: flex-start; gap: 0.75rem; flex: 1;`;
const DropoffNum = styled.div`
  width: 28px; height: 28px; border-radius: 50%;
  background: #f0f4ff; color: #84a4fc;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.8rem; font-weight: 700; flex-shrink: 0;
`;
const DropoffInfo = styled.div``;
const DropoffLabel = styled.div`font-size: 0.9rem; font-weight: 600; color: #333;`;
const DropoffLost = styled.div`font-size: 0.78rem; color: #f02a4c; margin-top: 0.2rem;`;

const DropoffRight = styled.div`display: flex; align-items: center; gap: 1rem; flex-shrink: 0;`;
const DropoffValue = styled.div`font-size: 1.3rem; font-weight: 700; color: #333; min-width: 50px; text-align: right;`;
const DropoffBar = styled.div`width: 120px; height: 10px; background: #f0f0f0; border-radius: 5px; overflow: hidden;`;
const DropoffFill = styled.div`height: 100%; width: ${p => p.$pct}%; background: ${p => p.$color}; border-radius: 5px;`;
const DropoffPct = styled.div`font-size: 0.8rem; min-width: 90px;`;

const IssueCard = styled.div`
  padding: 1rem 1.25rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  border-left: 4px solid ${p => p.$severity === 'high' ? '#f02a4c' : p.$severity === 'medium' ? '#e67e22' : '#27ae60'};
  background: ${p => p.$severity === 'high' ? '#fff5f7' : p.$severity === 'medium' ? '#fffaf0' : '#f0fff7'};
`;
const IssueSeverity = styled.div`font-size: 0.75rem; font-weight: 700; margin-bottom: 0.35rem; color: ${p => p.$severity === 'high' ? '#f02a4c' : p.$severity === 'medium' ? '#e67e22' : '#27ae60'};`;
const IssueTitle = styled.div`font-size: 0.9rem; font-weight: 600; color: #333; margin-bottom: 0.35rem;`;
const IssueDetail = styled.div`font-size: 0.8rem; color: #666;`;
